import { useSyncExternalStore } from 'react';
import { useSettingsStore, type ControlScheme } from '../../store/settingsStore';
import { COARSE, FINE, isTouchOnlyDevice, media } from './touchDevice';

export { isTouchOnlyDevice };

export type ResolvedScheme = 'desktop' | 'mobile';

/** Auto picks Mobile on a touch-only device; Desktop and Mobile are manual overrides. */
export function resolveControlScheme(setting: ControlScheme, touchOnly: boolean): ResolvedScheme {
  if (setting === 'auto') return touchOnly ? 'mobile' : 'desktop';
  return setting;
}

export function currentControlScheme(): ResolvedScheme {
  return resolveControlScheme(useSettingsStore.getState().controlScheme, isTouchOnlyDevice());
}

/**
 * Calls `listener` whenever the resolved scheme may have changed: the setting, or a pointer
 * device plugged in or out (Auto). Returns the unsubscribe.
 */
export function subscribeControlScheme(listener: () => void): () => void {
  const offStore = useSettingsStore.subscribe((s, prev) => {
    if (s.controlScheme !== prev.controlScheme) listener();
  });
  const lists = [media(COARSE), media(FINE)].filter((m): m is MediaQueryList => m !== null);
  for (const m of lists) m.addEventListener?.('change', listener);
  return () => {
    offStore();
    for (const m of lists) m.removeEventListener?.('change', listener);
  };
}

/** The resolved scheme, re-rendering when it changes; switching needs no reload. */
export function useControlScheme(): ResolvedScheme {
  return useSyncExternalStore(subscribeControlScheme, currentControlScheme);
}
